import { type ReactElement, useEffect, useState } from "react";
import { HTTPError } from "ky";

import {
  type ActionPlan,
  deleteActionPlan,
  listActionPlans,
  updateActionPlan,
} from "@/api/actionPlans";
import { Card } from "@/components/hifi/Card";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { RiskBadge } from "@/components/hifi/RiskBadge";
import { ShellLayout } from "@/components/shell/ShellLayout";

const STATUSES = ["PENDING", "IN_PROGRESS", "COMPLETED", "OVERDUE", "CANCELLED"];

function priorityLevel(
  p: string,
):
  | "Negligible"
  | "Low"
  | "Moderate"
  | "Medium"
  | "High"
  | "Extreme"
  | "Highest" {
  if (p === "HIGHEST") return "Highest";
  if (p === "HIGH") return "High";
  if (p === "MEDIUM") return "Medium";
  if (p === "LOW") return "Low";
  return "Negligible";
}

function statusVariant(
  s: string,
): "ok" | "warn" | "bad" | "accent" | "default" {
  if (s === "COMPLETED") return "ok";
  if (s === "IN_PROGRESS") return "accent";
  if (s === "OVERDUE") return "bad";
  if (s === "CANCELLED") return "default";
  return "warn";
}

export function ActionPlansPage(): ReactElement {
  const [plans, setPlans] = useState<ActionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("");

  async function refresh(statusFilter: string): Promise<void> {
    setLoading(true);
    try {
      setPlans(
        await listActionPlans(
          statusFilter ? { status: statusFilter } : undefined,
        ),
      );
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Load failed (${String(err.response.status)})`
          : "Load failed",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh(filter);
  }, [filter]);

  async function handleStatusChange(
    id: string,
    status: string,
  ): Promise<void> {
    try {
      await updateActionPlan(id, { status });
      await refresh(filter);
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Update failed (${String(err.response.status)})`
          : "Update failed",
      );
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!window.confirm("Delete this action plan?")) return;
    try {
      await deleteActionPlan(id);
      await refresh(filter);
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Delete failed (${String(err.response.status)})`
          : "Delete failed",
      );
    }
  }

  const overdue = plans.filter((p) => p.status === "OVERDUE").length;
  const inProgress = plans.filter((p) => p.status === "IN_PROGRESS").length;

  return (
    <ShellLayout
      active="tasks"
      topbar={{
        crumbs: ["Workspace", "Action plans"],
        title: "Action plans",
        subtitle: `${String(plans.length)} tasks`,
      }}
    >
      <div className="space-y-5 p-5">
        {error && (
          <div
            role="alert"
            className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
          >
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[11px] font-medium text-n-600">
            <span className="font-mono uppercase tracking-[0.3px]">Status</span>
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-1.5 text-[12px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            >
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            {overdue > 0 && (
              <Pill variant="bad">{overdue} overdue</Pill>
            )}
            {inProgress > 0 && (
              <Pill variant="accent">{inProgress} in progress</Pill>
            )}
          </div>
        </div>

        <Card>
          {loading ? (
            <p className="p-4 text-[13px] text-n-500">Loading…</p>
          ) : plans.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">No action plans.</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                  <th className="px-4 py-2">Action</th>
                  <th className="px-4 py-2">Priority</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Target</th>
                  <th className="px-4 py-2">Owner</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-b border-n-100 align-top last:border-0 ${
                      p.status === "OVERDUE"
                        ? "bg-bad-bg/40 hover:bg-bad-bg/60"
                        : "hover:bg-n-50"
                    }`}
                  >
                    <td className="px-4 py-2.5 font-medium text-n-900">
                      {p.action_required}
                      {p.evidence && (
                        <p className="mt-0.5 text-[11px] font-normal text-n-500">
                          Evidence: {p.evidence}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <RiskBadge level={priorityLevel(p.risk_priority)} />
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={statusVariant(p.status)}>{p.status}</Pill>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-600">
                      {p.target_date
                        ? new Date(p.target_date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-n-700">
                      {p.responsible_person ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <select
                          value={p.status}
                          onChange={(e) => {
                            void handleStatusChange(p.id, e.target.value);
                          }}
                          className="rounded-2 border border-n-200 bg-white px-2 py-1 text-[11px] text-n-800 shadow-1 focus:border-a-500 focus:outline-none"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        <IconBtn
                          name="x"
                          size="sm"
                          label="Delete"
                          onClick={() => {
                            void handleDelete(p.id);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </ShellLayout>
  );
}
